import { useTranslation } from "react-i18next";
import { BadgeCheck } from "lucide-react";

import { Badge } from "@/components/ui/badge";

import { CopyValueButton } from "./CopyValueButton";
import { IconTile } from "./IconTile";
import { SectionLabel } from "./SectionLabel";
import type { IProfileDetail } from "../types";

interface AccountDetailsProps {
  details: IProfileDetail[];
}

/**
 * The identity facts (employee id, email, phone…) as a description list —
 * `<dl>`/`<dt>`/`<dd>` is the correct semantic for label/value pairs, so screen
 * readers announce "Email, jane@acme.com" instead of two loose lines.
 *
 * Rows are static, so hover only warms the background and brings the copy
 * button up to full opacity; the icon tile deliberately stays muted so the row
 * never pretends to be clickable.
 */
export const AccountDetails = ({ details }: AccountDetailsProps) => {
  const { t } = useTranslation(["common"]);
  if (!details.length) return null;

  return (
    <section className="space-y-2">
      <SectionLabel>{t("common:chrome.account_details")}</SectionLabel>
      <dl className="divide-y divide-border overflow-hidden rounded-sm border border-border bg-surface">
        {details.map(
          ({ icon: Icon, label, value, copyable, badge, verified }) => (
            <div
              key={label}
              className="group flex items-center gap-3 px-3 py-2.5 transition-colors duration-150 hover:bg-muted/50"
            >
              <IconTile>
                <Icon size={15} />
              </IconTile>
              <div className="min-w-0 flex-1">
                <dt className="text-[10px] font-medium tracking-[0.08em] text-muted-foreground uppercase">
                  {label}
                </dt>
                <dd className="flex items-center gap-1.5">
                  <span
                    className="truncate text-sm font-medium tabular-nums"
                    title={value}
                  >
                    {value}
                  </span>
                  {verified && (
                    <BadgeCheck
                      size={14}
                      className="shrink-0 text-success"
                      aria-label={`${label} verified`}
                      data-tooltip-id="hrm-tooltip"
                      data-tooltip-content={`${label} verified`}
                    />
                  )}
                  {!verified && badge && (
                    <Badge
                      variant={badge.variant}
                      className="shrink-0 px-1.5 py-0 text-[10px]"
                    >
                      {badge.text}
                    </Badge>
                  )}
                </dd>
              </div>
              {copyable && (
                // Dimmed at rest so five rows of copy icons don't compete with the
                // values, but never hidden — a hover-only affordance is invisible
                // on touch and unreachable by keyboard.
                <span className="opacity-55 transition-opacity duration-150 group-hover:opacity-100 focus-within:opacity-100">
                  <CopyValueButton value={value} label={label} />
                </span>
              )}
            </div>
          ),
        )}
      </dl>
    </section>
  );
};
